/**
 * /hospitality/gaming-tax — Gaming Tax / GCB Reg-6 percentage-fee computation.
 *
 * Mirrors a casino's monthly gaming-tax accrual: gross gaming revenue (GGR =
 * slot win + table win) for the day and month-to-date, the Gaming Control
 * Board Reg-6 graduated bracket schedule applied to month-to-date GGR, and
 * the booked daily accrual (this month's fee minus the fee already accrued
 * through the prior day).
 *
 * All money fields arrive as decimal strings; `money()` formats them. The
 * rate / marginal_rate / effective_rate fields are already-formatted percent
 * strings and are rendered as-is.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import { PageHeader, StatCard, Panel, StatusPill, money } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/gaming-tax")({
  component: GamingTaxPage,
});

// ── Types — matches GET /api/hospitality/gaming-tax contract ───────────────
interface GamingWin {
  slot_win: string;
  table_win: string;
  ggr: string;
  through_prior: string | null;
}
interface GamingBracket {
  label: string;
  rate: string; // already formatted, e.g. "6.75%"
  base: string;
  tax: string;
}
interface GamingTaxResult {
  property: string;
  business_date: string | null;
  period: string | null;
  today: GamingWin | null;
  month_to_date: GamingWin | null;
  brackets: GamingBracket[];
  month_fee: string | null;
  fee_accrued_through_prior: string | null;
  today_accrual: string | null;
  marginal_rate: string | null; // already formatted, e.g. "6.75%"
  effective_rate: string | null; // already formatted, e.g. "6.73%"
  note: string | null;
  message?: string;
}

function GamingTaxPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-gaming-tax"],
    queryFn: () => api<GamingTaxResult>("/api/hospitality/gaming-tax"),
  });

  const data = q.data;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Gaming Tax — GCB Reg-6"
        subtitle={
          data
            ? `${data.property}${data.period ? ` · ${data.period}` : ""}`
            : "Gross gaming revenue + GCB Reg-6 percentage fee"
        }
        right={
          data && !data.message && data.marginal_rate ? (
            <StatusPill tone="info">Marginal {data.marginal_rate}</StatusPill>
          ) : undefined
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!q.isLoading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
            <br />
            <span className="text-xs">
              Gaming-tax results populate once gaming win is imported for a business date.
            </span>
          </div>
        )}

        {!q.isLoading && data && !data.message && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <StatCard label="Today's GGR" value={money(data.today?.ggr ?? null, 0)} />
              <StatCard label="Month-to-date GGR" value={money(data.month_to_date?.ggr ?? null, 0)} />
              <StatCard label="Month fee to date" value={money(data.month_fee, 0)} />
              <StatCard
                label="Today's accrual"
                value={money(data.today_accrual, 0)}
                tone="success"
                caption={data.marginal_rate ? `Marginal ${data.marginal_rate}` : undefined}
              />
            </div>

            {/* Today's win breakdown */}
            {data.today && (
              <Panel label="Today's win">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Slots</div>
                    <div className="mt-1 text-xl font-medium tabular-nums text-slate-900 dark:text-slate-100">
                      {money(data.today.slot_win)}
                    </div>
                  </div>
                  <div className="border-l border-slate-100 dark:border-slate-800 pl-3">
                    <div className="text-xs text-slate-500 dark:text-slate-400">Tables</div>
                    <div className="mt-1 text-xl font-medium tabular-nums text-slate-900 dark:text-slate-100">
                      {money(data.today.table_win)}
                    </div>
                  </div>
                </div>
              </Panel>
            )}

            {/* Reg-6 bracket computation */}
            <Panel
              label="Reg-6 graduated computation"
              right={
                <span className="text-xs text-slate-500 dark:text-slate-400">on month-to-date GGR</span>
              }
            >
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Bracket</th>
                    <th className="text-left py-1.5 px-3">Rate</th>
                    <th className="text-right py-1.5 px-3">GGR in bracket</th>
                    <th className="text-right py-1.5 pl-3">Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {data.brackets.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-12 text-center text-slate-500 dark:text-slate-400">
                        No bracket activity for this period.
                      </td>
                    </tr>
                  )}
                  {data.brackets.map((b, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3 text-slate-800 dark:text-slate-200">{b.label}</td>
                      <td className="py-1.5 px-3 tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {b.rate}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs">{money(b.base)}</td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-xs">{money(b.tax)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 dark:border-slate-600 font-medium">
                    <td className="py-2 pr-3" colSpan={3}>
                      TOTAL
                    </td>
                    <td className="py-2 pl-3 text-right tabular-nums">{money(data.month_fee)}</td>
                  </tr>
                </tbody>
              </table>
            </Panel>

            {/* Accrual explainer */}
            <Panel label="Today's accrual">
              <div className="text-sm text-slate-700 dark:text-slate-300 tabular-nums">
                month fee {money(data.month_fee)} − fee through prior day{" "}
                {money(data.fee_accrued_through_prior)} ={" "}
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {money(data.today_accrual)}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {data.effective_rate && (
                  <StatusPill tone="neutral">Effective {data.effective_rate}</StatusPill>
                )}
                {data.marginal_rate && (
                  <StatusPill tone="neutral">Marginal {data.marginal_rate}</StatusPill>
                )}
              </div>
            </Panel>

            {/* Note */}
            {data.note && (
              <div className="text-xs text-slate-500 dark:text-slate-400">{data.note}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
